import { useState } from 'react';
import { useNavigate, Link } from '@tanstack/react-router';
import { MapPin, Package, Eye, ArrowRightLeft, DollarSign, MessageSquare, Send, Share2 } from 'lucide-react';
import type { PublicProfile } from '@siliconvault/shared';
import { useAuth } from '../../hooks/useAuth';
import { useSendMessage } from '../../hooks/useMessaging';
import { useTranslation } from '../../i18n';
import { ShareCatalogModal } from './ShareCatalogModal';
import styles from './PublicProfileHeader.module.css';

interface Props {
  profile: PublicProfile;
}

export function PublicProfileHeader({ profile }: Props) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const sendMessage = useSendMessage();
  const [showMessageInput, setShowMessageInput] = useState(false);
  const [messageContent, setMessageContent] = useState('');
  const [showShareModal, setShowShareModal] = useState(false);
  const isOwnProfile = user?.userId === profile.id;
  const initials = profile.displayName.slice(0, 2).toUpperCase();

  return (
    <div className={styles.header}>
      <div className={styles.identity}>
        <div className={styles.avatar}>
          {profile.avatarUrl ? (
            <img src={profile.avatarUrl} alt={profile.displayName} className={styles.avatarImage} />
          ) : (
            initials
          )}
        </div>
        <div className={styles.identityBody}>
          <h1 className={styles.displayName}>
            {profile.displayName}
            {profile.aiTier === 'pro' && (
              <span
                style={{
                  marginLeft: '0.5rem',
                  fontSize: '0.65rem',
                  fontWeight: 600,
                  padding: '0.15rem 0.45rem',
                  borderRadius: '4px',
                  background: 'var(--accent, #3b82f6)',
                  color: '#fff',
                  verticalAlign: 'middle',
                  letterSpacing: '0.03em',
                }}
              >
                PRO
              </span>
            )}
          </h1>
          {profile.profileSlug && (
            <div className={styles.slug}>@{profile.profileSlug}</div>
          )}
          {profile.bio && <p className={styles.bio}>{profile.bio}</p>}
          {profile.location && (
            <span className={styles.location}>
              <span className={styles.locationIcon}>
                <MapPin size={12} strokeWidth={1.75} />
              </span>
              {profile.location}
            </span>
          )}
        </div>
      </div>
      {isOwnProfile && (
        <div className={styles.messageSection}>
          <button
            className={styles.messageBtn}
            onClick={() => setShowShareModal(true)}
          >
            <Share2 size={14} strokeWidth={2} />
            {t('share.shareCatalog')}
          </button>
        </div>
      )}
      {!isOwnProfile && user && (
        <div className={styles.messageSection}>
          {!showMessageInput ? (
            <button
              className={styles.messageBtn}
              onClick={() => setShowMessageInput(true)}
            >
              <MessageSquare size={14} strokeWidth={2} />
              {t('messages.sendMessage')}
            </button>
          ) : (
            <div className={styles.messageInputRow}>
              <input
                className={styles.messageInput}
                value={messageContent}
                onChange={(e) => setMessageContent(e.target.value)}
                placeholder={t('messages.placeholder')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    if (messageContent.trim() && !sendMessage.isPending) {
                      sendMessage.mutate(
                        { userId: profile.id, content: messageContent.trim() },
                        { onSuccess: () => navigate({ to: '/messages' }) }
                      );
                    }
                  }
                }}
                autoFocus
              />
              <button
                className={styles.messageSendBtn}
                disabled={!messageContent.trim() || sendMessage.isPending}
                onClick={() => {
                  if (!messageContent.trim()) return;
                  sendMessage.mutate(
                    { userId: profile.id, content: messageContent.trim() },
                    { onSuccess: () => navigate({ to: '/messages' }) }
                  );
                }}
              >
                <Send size={14} strokeWidth={2} />
              </button>
              <button
                className={styles.messageCancelBtn}
                onClick={() => { setShowMessageInput(false); setMessageContent(''); }}
              >
                {t('common.cancel')}
              </button>
            </div>
          )}
        </div>
      )}
      {!isOwnProfile && !user && (
        <div className={styles.messageSection}>
          <Link to="/login" className={styles.messageBtn}>
            <MessageSquare size={14} strokeWidth={2} />
            {t('share.joinToMessage')}
          </Link>
        </div>
      )}
      <div className={styles.stats}>
        <div className={styles.statCard}>
          <Package size={14} strokeWidth={1.75} />
          <span className={styles.statValue}>{profile.stats.totalItems}</span>
          <span className={styles.statLabel}>{t('community.items')}</span>
        </div>
        <div className={`${styles.statCard} ${styles.statCardShowcase}`}>
          <Eye size={14} strokeWidth={1.75} />
          <span className={styles.statValue}>{profile.stats.showcasedItems}</span>
          <span className={styles.statLabel}>{t('community.showcased')}</span>
        </div>
        <div className={`${styles.statCard} ${styles.statCardTrade}`}>
          <ArrowRightLeft size={14} strokeWidth={1.75} />
          <span className={styles.statValue}>{profile.stats.forTradeItems}</span>
          <span className={styles.statLabel}>{t('community.forTrade')}</span>
        </div>
        <div className={`${styles.statCard} ${styles.statCardSale}`}>
          <DollarSign size={14} strokeWidth={1.75} />
          <span className={styles.statValue}>{profile.stats.forSaleItems}</span>
          <span className={styles.statLabel}>{t('community.forSale')}</span>
        </div>
      </div>

      {showShareModal && <ShareCatalogModal onClose={() => setShowShareModal(false)} />}
    </div>
  );
}
